import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { UploadCloud, Trash2, Copy, Image as ImageIcon, Loader2, Search, X, Tag as TagIcon, RefreshCw, ExternalLink, Save } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [detail, setDetail] = useState(null); // media item currently open in the side drawer
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/media`, { withCredentials: true });
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load media");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        await axios.post(`${API}/admin/media/upload`, form, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (e) {
        toast.error(`${file.name}: ${e?.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
    if (ok) toast.success(`Uploaded ${ok} file${ok === 1 ? "" : "s"}`);
    load();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  // All distinct tags across the collection (for filter chips)
  const allTags = useMemo(() => {
    const s = new Set();
    for (const m of items) (m.tags || []).forEach((t) => s.add(t));
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((m) => {
      if (activeTag && !(m.tags || []).includes(activeTag)) return false;
      if (!q) return true;
      return (
        (m.original_filename || "").toLowerCase().includes(q) ||
        (m.alt || "").toLowerCase().includes(q) ||
        (m.tags || []).some((t) => t.includes(q))
      );
    });
  }, [items, query, activeTag]);

  const patchItem = (updated) => {
    setItems((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
    if (detail?.id === updated.id) setDetail((d) => ({ ...d, ...updated }));
  };

  return (
    <div data-testid="admin-media">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Content</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-tight tracking-tight">Asset library</h1>
          <p className="mt-3 text-[#A9A9A6] text-sm max-w-lg">
            Every uploaded image and PDF. Search, tag, edit alt text, see where each asset is used, replace pixels in place — every existing <code>&lt;img src&gt;</code> keeps working.
          </p>
        </div>
        <span className="text-xs text-[#A9A9A6]" data-testid="media-total">
          {filtered.length} of {items.length} files
        </span>
      </div>

      {/* Drop zone */}
      <label
        htmlFor="media-file-input"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="media-dropzone"
        className={`mt-8 block border border-dashed p-8 lg:p-12 text-center cursor-pointer transition-colors ${
          dragOver ? "border-[#D8A936] bg-[#151517]" : "border-[#343437] bg-[#0B0B0C] hover:border-[#D8A936]"
        }`}
      >
        <input
          id="media-file-input"
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="sr-only"
          onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
          data-testid="media-file-input"
        />
        <div className="flex flex-col items-center gap-3 text-[#A9A9A6]">
          {uploading ? <Loader2 size={24} className="text-[#D8A936] animate-spin" /> : <UploadCloud size={24} className="text-[#D8A936]" />}
          <p className="font-display text-base text-[#FAF9F6]">
            {uploading ? "Uploading…" : "Drop files here or click to choose"}
          </p>
          <p className="text-xs">Images (jpg, png, webp, gif) or PDF · max 15 MB each</p>
        </div>
      </label>

      {/* Search + tag chips */}
      <div className="mt-8 space-y-4">
        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A9A9A6]" />
          <input
            type="search"
            placeholder="Search filename, alt text or tag…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="media-search"
            className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] pl-9 pr-3 py-2.5 outline-none focus:border-[#D8A936] text-sm"
          />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A9A9A6] hover:text-[#FAF9F6]" aria-label="Clear search">
              <X size={14} />
            </button>
          )}
        </div>
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="media-tag-chips">
            <button
              onClick={() => setActiveTag(null)}
              className={`px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase border transition-colors ${
                activeTag === null
                  ? "border-[#D8A936] text-[#D8A936] bg-[#D8A936]/5"
                  : "border-[#343437] text-[#A9A9A6] hover:border-[#FAF9F6] hover:text-[#FAF9F6]"
              }`}
            >
              All
            </button>
            {allTags.map((t) => (
              <button
                key={t}
                onClick={() => setActiveTag(t === activeTag ? null : t)}
                data-testid={`media-tag-${t}`}
                className={`px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase border transition-colors inline-flex items-center gap-1.5 ${
                  activeTag === t
                    ? "border-[#D8A936] text-[#D8A936] bg-[#D8A936]/5"
                    : "border-[#343437] text-[#A9A9A6] hover:border-[#FAF9F6] hover:text-[#FAF9F6]"
                }`}
              >
                <TagIcon size={10} /> {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="mt-10 text-[#A9A9A6]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 border border-[#343437] p-10 text-center text-[#A9A9A6]" data-testid="media-empty">
          <ImageIcon size={20} className="mx-auto text-[#D8A936] mb-3" />
          {items.length === 0 ? "No files yet. Drop your first image above." : "No files match your search/filter."}
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="media-grid">
          {filtered.map((m) => {
            const isImage = (m.content_type || "").startsWith("image/");
            return (
              <button
                type="button"
                key={m.id}
                onClick={() => setDetail(m)}
                className="group border border-[#343437] bg-[#151517] hover:border-[#D8A936] transition-colors text-left"
                data-testid={`media-item-${m.id}`}
              >
                <div className="aspect-[4/3] bg-[#0B0B0C] overflow-hidden flex items-center justify-center relative">
                  {isImage ? (
                    <img src={m.url} alt={m.alt || m.original_filename} loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon size={40} className="text-[#343437]" />
                  )}
                  {(m.tags || []).length > 0 && (
                    <span className="absolute bottom-2 left-2 text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 bg-[#0B0B0C]/80 border border-[#D8A936] text-[#D8A936]">
                      {m.tags.length} tag{m.tags.length === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
                <div className="p-3 border-t border-[#343437]">
                  <p className="text-xs text-[#FAF9F6] truncate" title={m.original_filename}>{m.original_filename}</p>
                  <p className="text-[10px] text-[#A9A9A6] mt-1">{Math.round((m.size || 0) / 1024)} KB</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {detail && (
        <DetailDrawer
          media={detail}
          onClose={() => setDetail(null)}
          onPatched={patchItem}
          onDeleted={(id) => {
            setItems((prev) => prev.filter((m) => m.id !== id));
            setDetail(null);
          }}
          onCopy={copyUrl}
        />
      )}
    </div>
  );
}

function DetailDrawer({ media, onClose, onPatched, onDeleted, onCopy }) {
  const [tagsInput, setTagsInput] = useState((media.tags || []).join(", "));
  const [alt, setAlt] = useState(media.alt || "");
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const replaceRef = useRef();

  useEffect(() => {
    // Refetch usage on open.
    axios.get(`${API}/admin/media/${media.id}/usage`, { withCredentials: true })
      .then((r) => setUsage(r.data))
      .catch(() => setUsage({ count: 0, usages: [] }));
  }, [media.id]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
        alt,
      };
      const r = await axios.patch(`${API}/admin/media/${media.id}`, body, { withCredentials: true });
      onPatched(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const count = usage?.count || 0;
    const msg = count > 0
      ? `This asset is used in ${count} place${count === 1 ? "" : "s"}. Deleting will 404 every reference. Type DELETE to confirm.`
      : "Delete this file? (soft-delete — storage keeps the object)";
    const confirmed = count > 0
      ? window.prompt(msg) === "DELETE"
      : window.confirm(msg);
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/media/${media.id}${count > 0 ? "?force=true" : ""}`, { withCredentials: true });
      toast.success("Deleted");
      onDeleted(media.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const replace = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15 MB)");
      return;
    }
    setReplacing(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/admin/media/${media.id}/replace`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      onPatched({ ...r.data, url: r.data.url + `?v=${Date.now()}` });  // bust cache
      toast.success("Pixels replaced — same URL, new content");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Replace failed");
    } finally {
      setReplacing(false);
    }
  };

  const isImage = (media.content_type || "").startsWith("image/");

  return (
    <div className="fixed inset-0 z-[85] bg-[#0B0B0C]/85 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="media-detail">
      <div className="bg-[#151517] border-l border-[#343437] w-full max-w-lg h-full overflow-auto p-6 space-y-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow">Asset</p>
            <h2 className="mt-2 font-display text-xl text-[#FAF9F6] break-all">{media.original_filename}</h2>
            <p className="text-[11px] text-[#A9A9A6] mt-1">
              {Math.round((media.size || 0) / 1024)} KB · {media.content_type}
            </p>
          </div>
          <button onClick={onClose} className="text-[#A9A9A6] hover:text-[#FAF9F6]" aria-label="Close" data-testid="media-detail-close">
            <X size={20} />
          </button>
        </div>

        <div className="bg-[#0B0B0C] border border-[#343437] aspect-[4/3] overflow-hidden flex items-center justify-center">
          {isImage ? (
            <img src={media.url} alt={media.alt || media.original_filename} className="w-full h-full object-contain" />
          ) : (
            <ImageIcon size={40} className="text-[#343437]" />
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onCopy(media.url)}
            data-testid="media-detail-copy"
            className="flex-1 flex items-center justify-center gap-1.5 border border-[#343437] px-3 py-2 text-[10px] tracking-[0.18em] uppercase text-[#FAF9F6] hover:border-[#D8A936] hover:text-[#D8A936] transition-colors"
          >
            <Copy size={12} /> Copy URL
          </button>
          <button
            onClick={() => replaceRef.current?.click()}
            data-testid="media-detail-replace"
            disabled={replacing}
            className="flex-1 flex items-center justify-center gap-1.5 border border-[#343437] px-3 py-2 text-[10px] tracking-[0.18em] uppercase text-[#FAF9F6] hover:border-[#D8A936] hover:text-[#D8A936] transition-colors disabled:opacity-50"
          >
            {replacing ? <><Loader2 size={12} className="animate-spin" /> Replacing</> : <><RefreshCw size={12} /> Replace</>}
          </button>
          <input
            ref={replaceRef}
            type="file"
            hidden
            accept={media.content_type}
            onChange={(e) => replace(e.target.files?.[0])}
          />
          <button
            onClick={doDelete}
            data-testid="media-detail-delete"
            className="p-2 border border-[#343437] text-[#A9A9A6] hover:border-red-400 hover:text-red-400 transition-colors"
            aria-label="Delete"
          >
            <Trash2 size={12} />
          </button>
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="eyebrow block mb-2">Alt text</span>
            <input
              type="text"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="Describe the image for screen readers"
              data-testid="media-detail-alt"
              className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2 text-sm outline-none focus:border-[#D8A936]"
            />
          </label>
          <label className="block">
            <span className="eyebrow block mb-2">Tags (comma-separated)</span>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="volunteer, calgary-pride, portrait"
              data-testid="media-detail-tags"
              className="w-full bg-transparent border border-[#343437] text-[#FAF9F6] px-3 py-2 text-sm outline-none focus:border-[#D8A936]"
            />
            <span className="text-[10px] text-[#A9A9A6] mt-1 block">Lowercased on save. Used for filter chips.</span>
          </label>
          <button
            onClick={save}
            disabled={saving}
            data-testid="media-detail-save"
            className="btn-primary w-full justify-center disabled:opacity-50"
          >
            {saving ? <><Loader2 size={12} className="animate-spin" /> Saving</> : <><Save size={12} /> Save changes</>}
          </button>
        </div>

        <div className="pt-4 border-t border-[#343437]">
          <p className="eyebrow mb-3">
            Used in {usage === null ? "…" : usage.count} place{usage?.count === 1 ? "" : "s"}
          </p>
          {usage === null ? (
            <p className="text-xs text-[#A9A9A6]">Scanning…</p>
          ) : usage.count === 0 ? (
            <p className="text-xs text-[#A9A9A6] italic">Not referenced anywhere yet.</p>
          ) : (
            <ul className="space-y-2" data-testid="media-detail-usages">
              {usage.usages.map((u) => (
                <li key={`${u.collection}-${u.id}-${u.field}`} className="flex items-center justify-between gap-3 text-xs border border-[#343437] px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-[#FAF9F6] truncate">{u.label}</p>
                    <p className="text-[10px] text-[#A9A9A6]">{u.collection} · {u.field}</p>
                  </div>
                  {u.admin_link && (
                    <Link
                      to={u.admin_link}
                      className="shrink-0 inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-[#D8A936] hover:underline"
                    >
                      Open <ExternalLink size={10} />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
